import React, { useState, useEffect } from 'react';
import { Box, Typography, useMediaQuery, useTheme, Link } from '@mui/material';
import ConsentAcceptedIcon from '../../assets/ConsetAccepted.svg';
import ConsentRejectedIcon from '../../assets/ConsentRejected.svg';
import InfoIcon from '../../assets/InfoIcon.svg';
import Footer from '../Sections/Footer';
import TrustMarker from '../common/TrustMarker';
import StepperBar from '../common/Stepper';
import { useTranslation } from '../../hooks/useTranslation';

const ConsentRenewRedirectionCard = (props) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('lg'));
	const isDark =
		props.customDetails?.darkTheme === true || theme.palette.mode === 'dark';

	// Dynamically calculate header height
	const [headerHeight, setHeaderHeight] = useState(70);

	useEffect(() => {
		const calculateHeaderHeight = () => {
			const appBar = document.querySelector('header.MuiAppBar-root');
			if (appBar) {
				const height = appBar.offsetHeight;
				setHeaderHeight(height);
			}
		};

		calculateHeaderHeight();

		window.addEventListener('resize', calculateHeaderHeight);

		const observer = new MutationObserver(calculateHeaderHeight);
		const appBar = document.querySelector('header.MuiAppBar-root');
		if (appBar) {
			observer.observe(appBar, {
				childList: true,
				subtree: true,
				attributes: true,
			});
		}

		return () => {
			window.removeEventListener('resize', calculateHeaderHeight);
			observer.disconnect();
		};
	}, []);

	const isSuccess = props.statusCode === 200;
	const isDenied = props.statusCode === 201;
	const isError = props.statusCode === 20;
	const isRevoked = props.statusCode === 202;

	const fiuName = props.fiuName;

	const handleRedirect = () => {
		window.open('https://aaweb.finvu.in/', '_blank');
	};

	const getTitle = () => {
		if (isSuccess) {
			return t('consentRenewal.titles.successfullyRenewed');
		} else if (isDenied) {
			return t('consentRenewal.titles.deniedRenewal');
		} else if (isRevoked) {
			return t('consentRenewal.titles.alreadyRevoked');
		} else {
			return t('redirection.titles.somethingWentWrong');
		}
	};

	return (
		<Box
			sx={{
				display: 'flex',
				justifyContent: 'space-between',
				flexDirection: 'column',
				minHeight: '100vh',
				alignItems: 'center',
				paddingTop: '0',
				paddingLeft: '0',
				paddingRight: '0',
				width: '100%',
				backgroundColor: isDark ? '#161616' : '#fff',
				paddingBottom: '80px',
			}}
		>
			<Box
				sx={{
					width: '100%',
					paddingTop: `${headerHeight}px`,
					paddingBottom: 0,
					paddingLeft: 0,
					paddingRight: 0,
					backgroundColor: isDark ? '#2D2D2D' : '#fff',
					marginBottom: '0px',
				}}
			>
				<Box
					sx={{
						width: '100%',
						'& > div': {
							padding: '12px 20px !important',
						},
					}}
				>
					<TrustMarker
						customDetails={props.customDetails}
						noBorderRadius={true}
					/>
				</Box>
			</Box>
			<Box
				sx={{
					width: isMobileOrTablet ? '95%' : '83.5%',
					margin: '0 auto',
					marginTop: '-12px',
					'& > div': { paddingTop: '30px !important' },
				}}
			>
				<StepperBar
					customDetails={props.customDetails}
					progressPercentage={100}
				/>
			</Box>
			<Box
				sx={{
					padding: '2rem 1.5rem',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					textAlign: 'center',
					gap: '1rem',
					borderRadius: '8px',
					maxWidth: isMobileOrTablet ? '90%' : '50%',
					width: isMobileOrTablet ? '90%' : '50%',
					border: `1px solid ${isDark ? '#2D2D2D' : '#e0e0e0'}`,
					height: isMobileOrTablet ? '72vh' : '70vh',
					backgroundColor: isDark ? '#161616' : '#fff',
					margin: 'auto',
					marginTop: '1.2rem',
				}}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						flex: 1,
						justifyContent: 'center',
					}}
				>
					<Box
						sx={{
							width: '3rem',
							height: '3rem',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							marginBottom: '1.75rem',
						}}
					>
						<img
							src={
								isSuccess
									? ConsentAcceptedIcon
									: isRevoked || isError
									? InfoIcon
									: ConsentRejectedIcon
							}
							alt={
								isSuccess ? 'Success' : isRevoked || isError ? 'Info' : 'Error'
							}
							style={{
								width: '3.625rem',
								height: '3.625rem',
							}}
						/>
					</Box>

					<Box
						sx={{
							display: 'flex',
							flexDirection: 'row',
							alignItems: 'center',
							justifyContent: 'center',
							gap: '0.25rem',
							flexWrap: 'wrap',
						}}
					>
						<Typography
							sx={{
								fontSize: '1rem',
								fontWeight: 600,
								color: isDark ? '#FFFFFF' : '#000',
								textAlign: 'center',
								lineHeight: 1.4,
							}}
						>
							{getTitle()}
						</Typography>
						{(isSuccess || isDenied) && fiuName && (
							<Typography
								sx={{
									fontSize: '1rem',
									fontWeight: 600,
									color: isDark ? '#FFFFFF' : '#000',
									textAlign: 'center',
									lineHeight: 1.4,
								}}
							>
								{fiuName}
							</Typography>
						)}
					</Box>
				</Box>

				{global.isRedirect === 'true' && props.isCloseWindow === false ? (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							marginTop: 'auto',
							pt: 2,
						}}
					>
						<Typography
							sx={{
								textAlign: 'center',
								color: isDark ? '#7A8185' : '#666',
								fontSize: isMobileOrTablet ? '0.75rem' : '0.875rem',
							}}
						>
							{t('consentRenewal.messages.redirectIn')} {props.time}...
						</Typography>
					</Box>
				) : (
					<Box
						sx={{
							marginTop: 'auto',
							pt: 1,
						}}
					>
						<Typography
							sx={{
								textAlign: 'center',
								color: isDark ? '#7A8185' : '#666',
								fontSize: isMobileOrTablet ? '0.875rem' : '1rem',
							}}
						>
							{t('consentRenewal.messages.closeWindow')}
						</Typography>
					</Box>
				)}
			</Box>

			<Box
				sx={{
					position: 'fixed',
					bottom: 0,
					left: 0,
					right: 0,
					zIndex: 1000,
				}}
			>
				<Footer customDetails={props.customDetails} />
			</Box>
		</Box>
	);
};

export default ConsentRenewRedirectionCard;
